import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import {
  BUTTON_SCHEME_CONFIG,
  BUTTON_SCHEME_MAP,
  BUTTONRANK_PRIMARY,
  BUTTONSHAPE_ICON,
  BUTTONSHAPE_TEXT,
  BUTTONSHAPE_TEXTANDICON,
  BUTTONSIZE_NORMAL,
  BUTTONSIZE_SMALL,
  BUTTONTYPE_BUTTON,
  BUTTONTYPE_SUBMIT,
  TARGET_SELF
} from "./constants/button-constants";
import {
  ButtonLocalization,
  ButtonRank,
  ButtonSchemeDefinition,
  ButtonSchemeName,
  ButtonShape,
  ButtonSize,
  ButtonTarget,
  ButtonType
} from "./constants/button-types";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { isMobile } from "../../utils/screen-helper";
import { ConditionalWrapper } from "../../common/conditional-wrapper";
import { computeLinkAriaLabel } from "../../utils/link-helper";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import Fragment from "../../common/fragment";
import pfx from "../../utils/style-class-prefix-helper";
import { openModal } from "../../utils/openModal";
import { BUTTON_LOCALIZATION } from "./constants/button-i18n";
import { ButtonSchemeConfiguration } from "../../constants/colors";
import { PreloaderSquaresSchemeName } from "../preloader-squares/constants/preloader-squares-types";
import {
  BUTTON_DEFAULT_ICON_POSITION,
  BUTTON_ICON_POSITION_LEFT,
  BUTTON_ICON_POSITION_RIGHT,
  ButtonIconPosition
} from "../../constants/global-constants";
import { ICONSIZE_NORMAL, ICONSIZE_SMALL } from "../icon/constants/icon-constants";

import { BackgroundColor } from "../background/constants/background-types";
import { StyledComponent } from "../../decorators/styled-component";
import sanitizeHtml from "sanitize-html";
// ENVIAM
import { KeyIds } from '../../constants/keys';
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [tooltip] - holds envc-eon-ui-tooltip component.
 */

@Component({
  tag: "envc-eon-ui-button",
  styleUrl: "button.scss",
  shadow: true
})
export class Button
  implements
    ColorableComponent<ButtonSchemeConfiguration<ButtonSchemeDefinition>>,
    LocalizableComponent<ButtonLocalization> {
  // @InitLogging() sentryTransaction;
  schemeConfig: ButtonSchemeConfiguration<ButtonSchemeDefinition> = BUTTON_SCHEME_CONFIG;
  schemeMap = BUTTON_SCHEME_MAP;

  @State() activeScheme: ButtonSchemeDefinition;

  @State() private shape: ButtonShape;

  localization: Localization<ButtonLocalization> = BUTTON_LOCALIZATION;
  currentLocalization: ButtonLocalization;

  private currentIcon: string;
  private currentAriaLabel: string;

  // ENVIAM
  private link?: HTMLAnchorElement;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  /**
   * Raised when the button has been clicked.
   */
  @Event() buttonClick: EventEmitter;

  /**
   * Raised when the button has been loaded.
   */
  @Event() buttonLoad: EventEmitter;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEnvcEonUiButtonElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or envc-eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: ButtonSchemeName;

  /**
   * Whether this button is disabled or not. Disabled buttons can't be clicked and emit no events.
   */
  @Prop({ reflect: true }) disabled: boolean;

  /**
   * Whether this button can be focused by keyboard. Defaults to true.
   */
  @Prop() navigable: boolean = true;

  /**
   * Use primary whenever the button is the main CTA in the current context.
   * Secondary buttons should only appear next to primary buttons and are rendered as "outline" button.
   */
  @Prop() rank: ButtonRank = BUTTONRANK_PRIMARY;

  /**
   * The size of the button.
   */
  @Prop({ mutable: true }) size: ButtonSize = BUTTONSIZE_NORMAL;

  /**
   * The name of the icon (without file extension) to use in the button (optional).
   * @example "arrow_right", "phone", "notification", "social_x", "social_youtube", "social_facebook",
   * "social_instagram", "social_linked_in", "play", "video_replay"
   */
  @Prop() icon?: string;

  /**
   * A string set as content of the button.
   * You can also omit this and specify HTML content yourself.
   * If you specify both, the content is ignored and this property is used instead.
   * @example <envc-eon-ui-button text="CONTENT"/>
   * @example <envc-eon-ui-button>CONTENT</envc-eon-ui-button>
   */
  @Prop() text: string;

  /**
   * The name of the button must be unique per page.!
   *@example <envc-eon-ui-button name="button"></envc-eon-ui-input>
   */
  @Prop() name: string;

  /**
   * The type of the button. Defaults to "button" which means "client-side" handling.
   * Change this to "submit" or "reset" if the button is used in a form.
   */
  @Prop() inputType: ButtonType = BUTTONTYPE_BUTTON;

  /**
   * Specifies the label to use for accessibility.
   * Always provide this for pure icon buttons!
   */
  @Prop({ attribute: "a11y-aria-label", reflect: false, mutable: true }) a11yAriaLabel: string;

  /**
   * Full width of button on Mobile viewport
   */
  @Prop() fullWidthOnMobile: boolean = false;

  /**
   * This Prop will toggle the class full-width-on-mobile on basis of fullWidthOnMobile Prop
   */
  @Prop({ mutable: true }) toggleFullWidthOnMobile: boolean = false;

  /**
   * Defines the target url of a button.
   * Valid protocols are "http://", "https://", "mailto:" and "tel:".
   */
  @Prop({ reflect: true }) href: string;

  /**
   * Specifies how the link is opened in the browser, e.g. in new window/tab.
   * @example "_blank", "_self", "_parent", "_top"
   * @default "_self"
   */
  @Prop() target: ButtonTarget = TARGET_SELF;

  /**
   * If set to true, the "Download" icon will be used for the button as link and the browser will be forced to download the file instead of rendering it.
   * This is also the case if the href starts with "file:".
   */
  @Prop() download: boolean;

  /**
   * Whether to use button as CTA link
   */
  @Prop({ reflect: true }) useAsLink: boolean;

  /**
   * use this property to open a modal window, the modal must have the same modal-id<br>
   * see component: Modal
   */
  @Prop() openModalId: string;

  /**
   * Show preloader
   */
  @Prop() preloader: boolean = false;

  /**
   * Overwrite the color scheme of the preloader.
   */
  @Prop() preloaderScheme: PreloaderSquaresSchemeName;

  /**
   * used to set icon position of the Button text. It can either be left or right.
   */
  @Prop() iconPosition: ButtonIconPosition = BUTTON_DEFAULT_ICON_POSITION;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Full width of button for all the viewport.
   */
  @Prop() fullWidth: boolean = false;

  /**
   * Specifies the selection of buttons to use for accessibility.
   */
  @Prop({ attribute: "a11y-aria-selected" }) a11yAriaSelected: boolean;

  /**
   * The aria-pressed attribute is only relevant for toggle buttons.
   * It represents the button's current "pressed" state.
   */
  @Prop({ attribute: "a11y-aria-pressed" }) a11yAriaPressed: boolean;

  @Watch("fullWidthOnMobile")
  @Listen("resize", { target: "window" })
  onWindowResize() {
    this.toggleFullWidthOnMobile = this.fullWidthOnMobile && isMobile();
  }

  componentWillLoad() {
    this.deprecatedFallback();
    this.applyScheme();
    this.setCurrentLocalization();
    this.computeShape();
    this.computeIcon();
    this.computeAriaLabel();
    this.onWindowResize();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.buttonLoad.emit();
  }

  deprecatedFallback() {
    this.a11yAriaLabel = this.hostElement.getAttribute("aria-label") ?? this.a11yAriaLabel;
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);

    switch (this.rank) {
      case "primary":
        this.innerBackground = this.activeScheme.primaryBackgroundColor;
        break;
      case "secondary":
        this.innerBackground = this.activeScheme.secondaryBackgroundColor;
        break;
      case "functional":
        this.innerBackground = this.activeScheme.functionalBackgroundColor;
        break;
      case "discreet":
        this.innerBackground = this.activeScheme.discreetBackgroundColor;
        break;
      default:
        this.innerBackground = this.activeScheme.primaryBackgroundColor;
        break;
    }
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  @Watch("href")
  @Watch("icon")
  @Watch("download")
  @Watch("text")
  private computeIcon() {
    if (this.icon) {
      this.currentIcon = this.icon;
      return;
    }
    // ENVIAM kein Icon
    // this.currentIcon = ComputeLinkIcon(this.href, this.download, "button");
  }

  @Watch("text")
  @Watch("icon")
  @Watch("href")
  private computeShape() {
    const hasText = this.text != null && this.text.length > 0;
    const hasIcon = this.icon != null && this.icon.length > 0;

    let shape: ButtonShape = BUTTONSHAPE_TEXT;
    if (hasText && hasIcon) {
      shape = BUTTONSHAPE_TEXTANDICON;
    } else if (!hasText && hasIcon) {
      shape = BUTTONSHAPE_ICON;
    }
    this.shape = shape;
  }

  @Watch("href")
  @Watch("preloader")
  @Watch("a11yAriaLabel")
  @Watch("download")
  private computeAriaLabel() {
    // When preloading aria-label should always indicate the loading state
    if (this.preloader) {
      this.currentAriaLabel = this.currentLocalization.ariaLabel.loading;
      return;
    }

    // If component is not indicating preloading, check whether aria label is set.
    if (this.a11yAriaLabel) {
      this.currentAriaLabel = this.a11yAriaLabel;
      return;
    }

    // If not label is set, check whether url is connected to mail, download or anything like this.
    this.currentAriaLabel = computeLinkAriaLabel(this.currentLocalization, this.href, this.download);
  }

  // ENVIAM - KeyDownEvents bei useAsLink
  private handleKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case KeyIds.SPACE:
      case KeyIds.ENTER:
        e.preventDefault();
        this.handleClick(e);
        break;
    }
  }

  handleClick(_event: Event) {
    if (this.inputType === BUTTONTYPE_SUBMIT) {
      // add a fake button to the enclosing form, click it and then remove it to be able to submit through the ShadowDOM.
      // See https://www.hjorthhansen.dev/shadow-dom-and-forms/.
      const form = this.hostElement.closest("form");
      if (form != null) {
        // event.preventDefault();
        const fakeSubmit = document.createElement("button");
        fakeSubmit.type = BUTTONTYPE_SUBMIT;
        fakeSubmit.style.display = "none";
        form.appendChild(fakeSubmit);
        fakeSubmit.click();
        fakeSubmit.remove();
      }
    }
    if (!!this.openModalId) {
      openModal(this.openModalId);
    }
    // ENVIAM: Tastatursteuerung verbessert, wenn es komplett als Link verwendet wird
    if(this.useAsLink && this.href && this.link) {
      this.link.click();
    } else {
      this.buttonClick.emit();
    }
  }

  renderButtonContent = () => {
    let iconRender = () => {
      return (
        this.currentIcon && (
          <envc-eon-ui-icon
            class={pfx({ icon: true, [`icon-${this.iconPosition}`]: true })}
            name={this.currentIcon}
            size={this.size === BUTTONSIZE_SMALL ? ICONSIZE_SMALL : ICONSIZE_NORMAL}
            alignmentX="center"
            alignmentY="center"
            noSpace={this.shape === BUTTONSHAPE_ICON ? false : true}
            aria-hidden="true"
          ></envc-eon-ui-icon>
        )
      );
    };

    return (
      <Fragment>
        {this.iconPosition === BUTTON_ICON_POSITION_LEFT && iconRender()}
        {this.preloader ? (
          <envc-eon-ui-preloader-squares scheme={this.preloaderScheme}></envc-eon-ui-preloader-squares>
        ) : (
          <span class={pfx({ "font-base-correction": true })} innerHTML={sanitizeHtml(this.text)}></span>
        )}
        {this.iconPosition === BUTTON_ICON_POSITION_RIGHT && iconRender()}
      </Fragment>
    );
  };

  render() {
    return (
      <Host class={pfx({ [`full-width`]: this.toggleFullWidthOnMobile || this.fullWidth })}>
        <ConditionalWrapper
          condition={!!this.useAsLink}
          wrapper={(children) => (
            <a
              class={pfx({
                // [`focus-${this.activeScheme.focusBorderColor}`]: !this.disabled && this.navigable,
                [this.rank]: true,
                [`${this.size}`]: !!this.size,
                "icon-only": this.shape === BUTTONSHAPE_ICON,
                [`full-width-mobile`]: this.toggleFullWidthOnMobile,
                [`full-width`]: this.fullWidth,
                [`disabled`]: this.disabled,
                [`loading`]: this.preloader
              })}
              {...(this.navigable ? { tabIndex: 0 } : {})}
              href={this.href}
              ref={(el) => (this.link = el as HTMLAnchorElement)}
              target={this.target}
              onKeyDown={(event: KeyboardEvent) => this.handleKeyDown(event)}
              {...(this.download ? { download: "" } : {})}
              {...(this.currentAriaLabel ? { "aria-label": this.currentAriaLabel } : {})}
              onClick={(event: MouseEvent) => this.handleClick(event)}
              aria-disabled={this.disabled}
            >
              {children}
            </a>
          )}
          defaultWrapper={(children) => (
            <button
              class={pfx({
                // [`focus-${this.activeScheme.focusBorderColor}`]: !this.disabled && this.navigable,
                [this.rank]: true,
                [`${this.size}`]: !!this.size,
                "icon-only": this.shape === BUTTONSHAPE_ICON,
                [`full-width-mobile`]: this.toggleFullWidthOnMobile,
                [`full-width`]: this.fullWidth,
                [`disabled`]: this.disabled,
                [`loading`]: this.preloader
              })}
              {...(this.currentAriaLabel ? { "aria-label": this.currentAriaLabel } : {})}
              {...(this.a11yAriaSelected ? { "aria-selected": this.a11yAriaSelected } : {})}
              {...(this.a11yAriaPressed ? { "aria-pressed": this.a11yAriaPressed } : {})}
              tabIndex={this.navigable ? 0 : -1}
              disabled={this.disabled || this.preloader}
              type={this.inputType}
              name={this.name}
              onClick={(event: MouseEvent) => this.handleClick(event)}
              aria-disabled={this.disabled}
            >
              {children}
            </button>
          )}
        >
          {this.renderButtonContent()}
        </ConditionalWrapper>
        <slot name="tooltip" />
      </Host>
    );
  }
}
